import { Box, Button, keyframes, styled } from "@mui/material";
import { colors } from "../../styles/styles";
import { Logo, ProfileIcon } from "../icons";
import { RowBox } from "../boxes";
import { Text } from "../text";
import { useContext, useMemo, useState } from "react";
import { handleSignOut } from "../../handlers/user";
import { GlobalContext } from "../../context/global-context";
import { WhiteButton as WhiteButtonStyled } from "../basic/inputs";
import { Link } from "react-router-dom";
import { getStripeLoginLink, openStripeLink } from "../../resources/stripe.resource";

const NavBox = styled(Box)({
	background: colors.grayMain,
	display: "flex",
	alignItems: "center",
	flexDirection: "row",
	height: 49,
	justifyContent: "space-between",
	width: "100%",
	position: "fixed",
	zIndex: 4,
});

const fadeOut = keyframes`
  from {
    opacity: 1;
  }

  to {
    opacity: 0;
  }
`;

const NavBoxInGame = styled(NavBox)(({ fullscreen }) => ({
	opacity: fullscreen ? 0 : 1,
	animation: `${fadeOut} 3s ease-in-out`,
	transition: "opacity 0.5s ease-in-out",
	"&:hover": {
		opacity: 1,
	},
}));

const NavMenuBox = styled(Box)({
	position: "fixed",
	top: 64,
	right: 15,
	zIndex: 4,
	backgroundColor: colors.grayMain,
	padding: 25,
	display: "flex",
	flexDirection: "column",
	gap: 15,
	alignItems: "center",
	borderRadius: 10,
	boxShadow: "0px 0px 7px 0px rgba(0, 0, 0, 0.20), 0px 0px 20px 0px rgba(0, 0, 0, 0.15)",
});

const RedButton = styled(Button)({
	padding: "5px 15px",
	width: "100%",
	borderRadius: 25,
	background: colors.redMain,
	textTransform: "none",
	"&:hover": {
		background: colors.redMain,
	},
	"&:active": {
		background: colors.redSecondary,
	},
});

const WhiteButton = styled(Button)({
	minWidth: 0,
	padding: "5px 15px",
	borderRadius: 25,
	background: colors.whiteMain,
	textTransform: "none",
	"&:hover": {
		background: colors.whiteMain,
	},
	"&:active": {
		background: colors.grayMain,
	},
});

const GraySimpleLogo = () => {
	return (
		<svg xmlns="http://www.w3.org/2000/svg" width="39" height="20" viewBox="0 0 39 20" fill="none">
			<path
				fillRule="evenodd"
				clipRule="evenodd"
				d="M3.44828 0C1.54385 0 0 1.54385 0 3.44828V16.5517C0 18.4562 1.54385 20 3.44828 20H35.4023C37.3067 20 38.8506 18.4562 38.8506 16.5517V3.44828C38.8506 1.54385 37.3067 0 35.4023 0H3.44828ZM24.2649 9.47777C24.3183 9.0775 24.6804 8.58967 25.0753 8.64387V8.64595C27.49 8.97743 33.1032 10.0427 33.1032 12.7592C33.1032 15.7007 26.223 17.2414 19.4251 17.2414C12.6271 17.2414 5.7469 15.7007 5.7469 12.7592C5.7469 10.1345 11.212 8.67723 16.8499 8.35201C16.854 8.35201 16.856 8.34575 16.856 8.34575V7.77453C16.856 7.77244 16.854 7.76827 16.8499 7.76827C13.528 7.22832 13.1537 5.92536 13.1537 5.36456C13.1537 3.57376 16.4056 2.75862 19.4271 2.75862C22.4487 2.75862 25.7006 3.57376 25.7006 5.36456C25.7006 5.92327 25.3262 7.22832 22.0044 7.76827C22.0003 7.76827 21.9982 7.77453 21.9982 7.77453V10.1282C21.9982 10.2324 22.0085 10.3388 22.027 10.443L22.1031 10.8683C22.1031 11.2811 21.6588 11.4249 21.2454 11.4062C20.8937 11.3895 20.3938 11.2623 20.4556 10.8183C20.5687 10.001 20.6098 7.95173 20.5584 7.45348C20.5584 7.08239 20.8299 6.65919 21.194 6.61541C22.9032 6.4111 23.944 5.97539 24.3081 5.65017C24.4829 5.49381 24.4747 5.22905 24.2978 5.10813C23.7774 4.75373 22.2471 4.00739 19.6308 4.00739C17.29 4.00739 15.842 4.68701 15.1982 5.06227C14.9432 5.21029 14.937 5.58971 15.1941 5.73564C15.7474 6.05253 16.5722 6.25058 17.1893 6.34231C17.6212 6.40693 17.975 6.72173 18.0963 7.14702C18.3756 8.1285 18.3233 9.7895 18.2827 11.0791C18.2587 11.8411 18.2388 12.4734 18.2938 12.7592C18.3699 13.1553 17.9709 13.2804 17.5739 13.2804C17.3764 13.2804 17.1152 13.2616 16.9054 13.1907C16.673 13.1136 16.5105 12.8988 16.5352 12.7988C16.8666 11.499 16.8555 10.0285 16.8541 9.83521C16.854 9.82698 16.854 9.82107 16.854 9.81758C16.854 9.81341 16.8499 9.81341 16.8478 9.81341C10.6196 10.1782 8.00945 11.4958 8.00945 12.5507C8.00945 13.6056 10.9796 15.7404 19.0137 15.7404C27.0478 15.7404 31.9164 14.1518 31.8691 12.5507C31.8341 11.3811 26.7825 9.99687 24.5117 10.3826C24.134 10.4467 24.205 9.92106 24.2586 9.52469C24.2607 9.50883 24.2628 9.49317 24.2649 9.47777Z"
				fill="black"
				fillOpacity="0.5"
			/>
		</svg>
	);
};

function DeveloperNavBar({ testOpen, fullScreen, setFullScreen, onExit }) {
	const { auth, stripe } = useContext(GlobalContext);
	const user = useMemo(() => {
		return auth.user;
	}, [auth]);
	const [openMenu, setOpenMenu] = useState(false);

	if (testOpen) {
		return (
			<NavBoxInGame fullscreen={fullScreen}>
				<RowBox gap={10}>
					<Box style={{ width: 15 }} />
					<RowBox gap={10} style={{ cursor: "pointer" }} onClick={onExit}>
						<svg width={19.5} height={16.5} viewBox="0 0 26 22" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M14.3048 3.30486L11 0L0 11L11 22L14.3048 18.6951L8.94664 13.3369H25.1158V8.66313H8.94658L14.3048 3.30486Z"
								fill={colors.redMain}
							/>
						</svg>
						<Text size={20} weight={700} color={colors.redMain}>
							Exit
						</Text>
					</RowBox>
				</RowBox>
				<Logo color={colors.redMain} />
				<RowBox gap={10}>
					<Box style={{ width: 37.7 }} />
					{fullScreen ? (
						<svg
							width={19.5}
							height={19.5}
							viewBox="0 0 26 26"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
							style={{ cursor: "pointer" }}
							onClick={() => setFullScreen(false)}
						>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M22.5637 0H25.9999V3.24909L22.2175 7.03154L24.7245 9.53855H16.3678V1.18183L18.8748 3.68885L22.5637 0ZM-5.34058e-05 3.34197V0H3.34335L7.03227 3.68892L9.53924 1.18195V9.53867H1.18252L3.68958 7.03161L-5.34058e-05 3.34197ZM3.24876 26H-5.34058e-05V22.5634L3.68902 18.8744L1.18204 16.3674H9.53876V24.7241L7.03171 22.2171L3.24876 26ZM25.9999 22.6568V26H22.6577L18.8748 22.217L16.3678 24.724V16.3673H24.7245L22.2175 18.8744L25.9999 22.6568Z"
								fill={colors.redMain}
							/>
						</svg>
					) : (
						<svg
							width={19.5}
							height={19.5}
							viewBox="0 0 26 26"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
							style={{ cursor: "pointer" }}
							onClick={() => setFullScreen(true)}
						>
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M25.9999 0V8.38706L23.4837 5.87088L19.2901 10.0645L15.9353 6.70964L20.1289 2.51606L17.6128 0H25.9999ZM2.66137e-05 0.000117868H8.38708L5.87092 2.51628L10.0644 6.70977L6.70959 10.0646L2.5161 5.8711L2.66137e-05 8.38718V0.000117868ZM25.9999 25.9999H17.6128L20.1289 23.4837L15.9354 19.2902L19.2903 15.9354L23.4838 20.1289L25.9999 17.6128V25.9999ZM0 17.6129V26H8.38706L5.87098 23.4839L10.0646 19.2903L6.70974 15.9355L2.51616 20.1291L0 17.6129Z"
								fill={colors.redMain}
							/>
						</svg>
					)}
					<Box style={{ width: 15 }} />
				</RowBox>
			</NavBoxInGame>
		);
	}

	const onSignOut = async () => {
		await handleSignOut();
		auth.logout();
	};

	const NavMenu = () => {
		const [stripeLoading, setStripeLoading] = useState(0);
		// const { stripe, auth } = useContext(GlobalContext);

		const ManageStripeAccount = async () => {
			setStripeLoading(1);
			const result = await getStripeLoginLink(auth.supaToken);
			if (result) setStripeLoading(2);
			else {
				setStripeLoading(3);
				setTimeout(() => {
					setStripeLoading(0);
				}, 1000);
			}
		};

		const CreateStripeAccount = async () => {
			setStripeLoading(1);
			const result = await openStripeLink(auth.supaToken);
			if (result) setStripeLoading(2);
			else {
				setStripeLoading(3);
				setTimeout(() => {
					setStripeLoading(0);
				}, 1000);
			}
		};

		return (
			<NavMenuBox>
				{auth.user.id ? (
					<>
						<WhiteButtonStyled
							width={"100%"}
							style={{ width: "100%" }}
							disabled={stripeLoading !== 0}
							disableRipple={true}
							onClick={() => (!stripe.isConnected ? CreateStripeAccount() : ManageStripeAccount())}
						>
							<Text size={14} weight={700} color={colors.blackMain}>
								{stripeLoading === 1
									? "Loading..."
									: stripeLoading === 2
										? "Launching Stripe"
										: stripeLoading === 3
											? "Couldn't launch Stripe"
											: !stripe.isConnected
												? "Set up payments"
												: "Manage payments"}
							</Text>
						</WhiteButtonStyled>
						<RedButton disableRipple={true} onClick={onSignOut}>
							<Text size={14} weight={700} color={colors.whiteMain}>
								Sign out
							</Text>
						</RedButton>
					</>
				) : (
					<Link to={"/"}>
						<RedButton disableRipple={true}>
							<Text size={14} weight={700} color={colors.whiteMain}>
								Sign in
							</Text>
						</RedButton>
					</Link>
				)}
			</NavMenuBox>
		);
	};

	return (
		<NavBox>
			<RowBox width={260} justify={"flex-start"}>
				<Box style={{ width: 25 }} />
				<Button disableRipple onClick={() => (window.location.href = "/")} style={{ padding: 0, backgroundColor: "transparent" }}>
					<GraySimpleLogo />
				</Button>
			</RowBox>
			<RowBox gap={25} justify={"flex-end"}>
				{auth.signedIn && user?.email && (
					<WhiteButton disableRipple={true} onClick={() => setOpenMenu(!openMenu)}>
						<RowBox gap={10}>
							<ProfileIcon handleClick={() => setOpenMenu(!openMenu)} color={colors.black50} />
							{!user.email ? null : (
								<Text weight={700} color={colors.black50}>
									{user.email}
								</Text>
							)}
						</RowBox>
					</WhiteButton>
				)}
				<Box />
			</RowBox>
			{openMenu ? <NavMenu /> : null}
		</NavBox>
	);
}

export default DeveloperNavBar;
